// Static navigation sitemap for Genie's search_pages / navigate_to tools.
// The current page describes itself via useGeniePage(); this list only needs
// to enumerate where the agent *can* navigate, including pages not currently
// mounted. Tab variants are encoded as ?tab=… so navigate_to can route to them.
export interface GeniePage {
  path: string;
  description: string;
  tags?: string[];
  // The dashboard sends workspaces on the v2 editor home from these.
  classicEditorOnly?: true;
}

export const GENIE_PAGES: GeniePage[] = [
  // ── Platform ────────────────────────────────────────────
  {
    path: "/?tab=flows",
    description: "Flows: the workspace's flows (Build › Flows in the sidebar)",
    tags: ["home", "dashboard", "flows", "workflows"],
  },
  {
    path: "/?tab=agents",
    description: "Agents: the workspace's agents (Build › Agents)",
    tags: ["agents", "assistants"],
  },
  {
    path: "/?tab=knowledge-bases",
    description:
      "Knowledge Bases: the workspace's knowledge bases (Build › Data)",
    tags: ["kbs", "knowledge bases", "data"],
  },
  {
    path: "/?tab=tables",
    description: "Tables: the workspace's tables (Build › Data)",
    tags: ["tables", "data", "spreadsheet"],
  },
  {
    path: "/templates",
    description:
      "Templates library: browse and deploy pre-built workflows and apps",
    tags: ["templates", "marketplace"],
    classicEditorOnly: true,
  },
  {
    path: "/apps",
    description: "App library: deployed apps and chat interfaces",
    tags: ["apps", "deployed"],
  },
  {
    path: "/task-manager",
    description: "Task manager: review labelling and approval tasks",
    tags: ["tasks"],
  },
  {
    path: "/past-usage?tab=flows",
    description: "Past usage: Flows, history of workflow runs",
    tags: ["usage", "history", "flows", "runs"],
  },
  {
    path: "/past-usage?tab=coworkers",
    description: "Past usage: Agents, history of agent conversations",
    tags: ["usage", "history", "agents", "conversations"],
  },
  {
    path: "/cases",
    description: "Cases: list of case-management cases",
    tags: ["cases", "case-management"],
  },
  {
    path: "/tasks",
    description: "Tasks: task list and assignments",
    tags: ["tasks"],
  },
  {
    path: "/changelog",
    description: "Changelog: product updates and release notes",
    tags: ["changelog", "releases", "updates"],
  },

  // ── Editors & Detail Views ──────────────────────────────
  {
    path: "/cases/:caseId",
    description: "Case detail: view and manage a single case",
    tags: ["case", "case-management", "detail"],
  },
  {
    path: "/tasks/:taskId",
    description: "Task detail: view a single task",
    tags: ["task", "detail"],
  },
  {
    path: "/inbox/:inboxId",
    description: "Inbox: case inbox view",
    tags: ["inbox", "cases"],
  },
  {
    path: "/inbox/:inboxId?tab=settings",
    description:
      "Inbox settings: name, prefix, icon, email source, case properties, export and delete",
    tags: ["inbox", "settings", "properties"],
  },
  {
    path: "/inbox/:inboxId?tab=automations",
    description: "Inbox automations: triage, extraction and the process flow",
    tags: ["inbox", "automations", "pipeline"],
  },
  {
    path: "/inbox/:inboxId/case-layout",
    description:
      "Case layout: arrange the fields an inbox's case page shows, on a sample case",
    tags: ["inbox", "case", "layout", "fields"],
  },
  {
    path: "/apps/c/:appId",
    description: "Chat app: a deployed chat application",
    tags: ["app", "chat", "deployed"],
  },
  {
    path: "/apps/d/:deploymentId",
    description: "Deployment: a deployed app or workflow",
    tags: ["app", "deployment", "deployed"],
  },
  {
    path: "/apps/f/:appId",
    description: "App: a deployed app",
    tags: ["app", "deployed"],
  },
  {
    path: "/templates/:templateId",
    description: "Template detail: preview a template before deploying",
    tags: ["template", "detail"],
    classicEditorOnly: true,
  },
  {
    path: "/templates/:templateId/customize",
    description:
      "Template customization: configure a template before deploying",
    tags: ["template", "customize", "configure"],
    classicEditorOnly: true,
  },
  {
    path: "/?tab=tables&source=platform",
    description:
      "Platform tables: read-only system tables over flows, runs, users, agents, conversations and knowledge bases",
    tags: ["tables", "platform", "system"],
  },
  {
    path: "/tables/schema",
    description:
      "Table schema builder: every workspace table as a diagram, with the columns and the relationships between tables",
    tags: ["tables", "schema", "relationships", "diagram", "columns"],
  },
  {
    path: "/tables/:tableId",
    description: "Table: a workspace data table's rows and columns",
    tags: ["table", "rows", "columns", "data"],
  },
  {
    path: "/flows/:flowId",
    description: "Workflow editor: build and edit a workflow with visual nodes",
    tags: ["editor", "workflow", "flow", "nodes"],
  },
  {
    path: "/flows/:flowId?tab=evals",
    description:
      "Flow tests: the flow's tests, each with its score and last run",
    tags: ["flow", "tests", "evaluations", "cases"],
  },
  {
    path: "/flows/:flowId?tab=evals&testSuiteId=:testSuiteId",
    description:
      "One flow test: its evaluators and cases with their status; test ids are suite ids from evaluations_list_workflow_test_suites",
    tags: ["flow", "test", "cases", "evaluators"],
  },
  {
    path: "/flows/:flowId?tab=evals&testSuiteId=:testSuiteId&testCaseId=:testCaseId",
    description:
      "One case of a flow test: its inputs and each evaluator's result; case ids from evaluations_list_test_cases",
    tags: ["flow", "case", "inputs", "results"],
  },
  {
    path: "/flows/:flowId?tab=analytics",
    description:
      "Workflow analytics: the flow's runs, errors, run time and cost, cost by node included",
    tags: ["workflow", "flow", "analytics", "usage", "cost", "runs"],
  },
  {
    path: "/assistant/:assistantId",
    description:
      "Assistant editor: configure an AI assistant's settings, tools, and instructions",
    tags: ["assistant", "agent", "configure"],
  },
  {
    path: "/assistant/:assistantId?version=:versionId",
    description:
      "Agent editor showing one saved version, read-only; version ids come from agents_list_versions",
    tags: ["agent", "version", "history", "saved"],
  },
  {
    path: "/assistant/:assistantId?section=Prompt",
    description: "Agent editor, Prompt section: persona, tone and instructions",
    tags: ["agent", "prompt", "persona", "tone", "instructions"],
  },
  {
    path: "/assistant/:assistantId?section=Actions",
    description:
      "Agent editor, Actions section: tools, quick actions, workflows, co-workers and skills",
    tags: ["agent", "actions", "tools", "skills", "workflows"],
  },
  {
    path: "/assistant/:assistantId?section=Actions&shell=sandbox",
    description:
      "Agent editor with the shell on the agent's own sandbox open; needs the Sandbox tool on and the agent saved",
    tags: ["agent", "sandbox", "shell", "terminal", "files", "command"],
  },
  {
    path: "/assistant/:assistantId?section=Knowledge",
    description:
      "Agent editor, Knowledge section: knowledge bases, tables, databases and files",
    tags: ["agent", "knowledge", "kb", "tables", "files", "databases"],
  },
  {
    path: "/assistant/:assistantId?section=Model",
    description:
      "Agent editor, Model section: model, reasoning and generation settings",
    tags: ["agent", "model", "llm", "reasoning", "temperature"],
  },
  {
    path: "/assistant/:assistantId?section=Display",
    description:
      "Agent editor, Display section: how replies and tool calls are shown",
    tags: ["agent", "display", "appearance"],
  },
  {
    path: "/assistant/:assistantId?tab=app",
    description:
      "Agent deployments: publish the agent to chat, Slack, Teams, a widget or an API",
    tags: [
      "agent",
      "deploy",
      "deployment",
      "publish",
      "channel",
      "widget",
      "slack",
      "teams",
    ],
  },
  {
    path: "/assistant/:assistantId?tab=past-runs",
    description: "Agent past chats: conversations users had with this agent",
    tags: ["agent", "past chats", "conversations", "history", "runs"],
  },
  {
    path: "/assistant/:assistantId?tab=analytics",
    description: "Agent analytics: usage, cost and feedback over time",
    tags: ["agent", "analytics", "usage", "cost", "metrics"],
  },
  {
    path: "/assistant/:assistantId?tab=insights",
    description: "Agent insights: topics and issues found in its conversations",
    tags: ["agent", "insights", "topics", "issues"],
  },
  {
    path: "/assistant/:assistantId?tab=tests",
    description:
      "Agent tests: the agent's test sets, each with its score, regressions and last run",
    tags: ["agent", "tests", "evaluations", "test sets", "golden set"],
  },
  {
    path: "/assistant/:assistantId?tab=tests&set=:testSetId",
    description:
      "One agent test set: two versions compared test by test; set ids from evaluations_list_agent_test_suites. Add &base=:runId&cand=:runId to open two runs (evaluations_list_runs)",
    tags: ["agent", "tests", "test set", "compare", "versions", "regressions"],
  },
  {
    path: "/assistant/:assistantId?tab=tests&set=:testSetId&test=:testId",
    description:
      "One agent test: its chat and its checks with their results; test ids from evaluations_list_test_cases",
    tags: ["agent", "test", "checks", "results"],
  },
  {
    path: "/assistant/:assistantId?tab=api",
    description: "Agent API: how to call this agent from code",
    tags: ["agent", "api", "sdk", "code", "integrate"],
  },
  {
    path: "/data-hub/:kbId",
    description: "Knowledge base: manage documents and data sources",
    tags: ["kb", "knowledge", "documents", "data"],
  },
  {
    path: "/data-hub/:kbId?tab=analytics",
    description:
      "Knowledge base analytics: documents uploaded and the AI operations their ingestion used",
    tags: ["kb", "knowledge", "analytics", "usage", "documents"],
  },
  {
    path: "/data-hub/:kbId?tab=advanced-settings",
    description:
      "Knowledge base advanced settings: retrieval config, chunking, embedding model",
    tags: ["kb", "settings", "retrieval", "advanced"],
  },
  {
    path: "/chat/:conversationId",
    description: "Chat conversation: full page chat view",
    tags: ["chat", "conversation"],
  },
  {
    path: "/chat/:conversationId?assistant_id=:assistantId",
    description:
      "One of an agent's conversations, full page, with the agent's name and controls; conversation ids come from conversations_list",
    tags: ["chat", "conversation", "agent", "past chat", "open"],
  },

  // ── Settings: Account ───────────────────────────────────
  {
    path: "/settings/profile",
    description:
      "User settings: profile (name, avatar, preferences) and advanced (authorized clients)",
    tags: ["settings", "profile", "account", "user"],
  },

  // ── Settings: Administration ────────────────────────────
  {
    path: "/settings/administration/users",
    description:
      "User management: invite, remove, and manage organization members",
    tags: ["settings", "users", "members", "invite"],
  },
  {
    path: "/settings/administration/workspaces",
    description: "Workspaces: list and manage workspaces in the organization",
    tags: ["settings", "workspaces"],
  },
  {
    path: "/settings/administration/workspaces/:workspaceId",
    description: "Manage workspace: manage a single workspace",
    tags: ["settings", "workspace", "detail"],
  },
  {
    path: "/manage-workspace?tab=api-keys",
    description:
      "API keys: create and manage API keys for the current workspace",
    tags: ["settings", "api", "keys", "tokens"],
  },
  {
    path: "/manage-workspace?tab=users",
    description: "Workspace members: manage users in the current workspace",
    tags: ["settings", "workspace", "users", "members"],
  },
  {
    path: "/manage-workspace?tab=linked-accounts",
    description:
      "Connected integrations: manage linked accounts and OAuth connections",
    tags: ["settings", "integrations", "oauth", "connected"],
  },
  {
    path: "/settings/administration/roles",
    description:
      "Roles: manage organization and workspace roles and permissions",
    tags: ["settings", "roles", "permissions"],
  },
  {
    path: "/settings/administration/billing",
    description:
      "Billing: manage subscription, view invoices, and usage limits",
    tags: ["settings", "billing", "subscription", "payment"],
  },
  {
    path: "/settings/administration/billing?tab=overview",
    description:
      "Billing overview: the plan's AI operations and model usage this cycle, and when it resets",
    tags: ["settings", "billing", "plan", "usage"],
  },
  {
    path: "/settings/administration/billing?tab=limits",
    description:
      "Billing usage controls: the organization's own operations and spend limits",
    tags: ["settings", "billing", "limits", "spend"],
  },
  {
    path: "/settings/administration/billing?tab=analytics",
    description:
      "Billing analytics: AI operations overage, and usage per workspace and per user",
    tags: ["settings", "billing", "analytics", "overage"],
  },
  {
    path: "/settings/observability/analytics",
    description: "Organization analytics: usage metrics and insights",
    tags: ["settings", "analytics", "metrics"],
  },
  {
    path: "/settings/observability/analytics?tab=flows",
    description:
      "Organization analytics, Flows tab: runs, errors, run time and cost of flows, by workspace or all",
    tags: ["settings", "analytics", "flows", "cost", "runs"],
  },
  {
    path: "/settings/observability/analytics?tab=agents",
    description:
      "Organization analytics, Agents tab: chats, messages, users and chat cost",
    tags: ["settings", "analytics", "agents", "chats", "cost"],
  },
  {
    path: "/settings/observability/analytics?tab=knowledge-bases",
    description:
      "Organization analytics, Knowledge bases tab: documents and ingestion AI operations",
    tags: ["settings", "analytics", "knowledge", "documents"],
  },
  {
    path: "/settings/observability/analytics?tab=api",
    description: "Organization analytics, API tab: API calls and their log",
    tags: ["settings", "analytics", "api", "calls"],
  },

  // ── Settings: Platform ──────────────────────────────────
  {
    path: "/settings/platform/models?tab=providers",
    description:
      "Model configuration: Providers. Manage LLM providers and their API keys",
    tags: ["settings", "models", "llm", "providers", "keys"],
  },
  {
    path: "/settings/platform/models?tab=llms",
    description: "Model configuration: LLMs. Enable and configure chat models",
    tags: ["settings", "models", "llm"],
  },
  {
    path: "/settings/platform/models?tab=embeddings",
    description:
      "Model configuration: Embeddings. Enable and configure embedding models",
    tags: ["settings", "models", "embeddings"],
  },
  {
    path: "/settings/platform/models?tab=presets",
    description:
      "Model presets: create and manage model presets for workflows and agents",
    tags: ["settings", "presets", "models"],
  },
  // Platform admins only: everyone else gets "Access Denied" here.
  {
    path: "/settings/platform/authentication",
    description:
      "Authentication (platform admins only): sign-in connections, allowed domains, session length and new-user workspaces",
    tags: ["settings", "authentication", "sso", "login", "domains", "admin"],
  },
  {
    path: "/settings/platform/integrations",
    description:
      "Integrations (platform admins only): every integration on the platform with its nodes and triggers",
    tags: ["settings", "integrations", "connectors", "admin"],
  },
  {
    path: "/settings/platform/rate-limits",
    description:
      "Rate limits (platform admins only): limits on the public API endpoints",
    tags: ["settings", "rate", "limits", "api", "admin"],
  },
  {
    path: "/settings/platform/authentication?tab=saml",
    description:
      "Authentication, Advanced (platform admins only): signup policy and the SAML access mapping, which SAML groups get which workspaces and roles",
    tags: ["settings", "saml", "sso", "mapping", "roles", "admin"],
  },
  {
    path: "/settings/platform/system",
    description:
      "System (platform admins only): build version, host health, audit logs, remote support access and automatic updates",
    tags: ["settings", "system", "audit", "logs", "updates", "admin"],
  },
  {
    path: "/settings/observability/platform-status",
    description:
      "Platform status (platform admins only): whether each platform service is operational, degraded or down",
    tags: ["settings", "status", "health", "services", "admin"],
  },
  {
    path: "/settings/observability/locks",
    description:
      "Database locks (platform admins only): locks held in the database right now",
    tags: ["settings", "database", "locks", "admin"],
  },
  {
    path: "/settings/organization/plugins",
    description:
      "Plugins (platform admins only): manage installed plugins and browse the marketplace",
    tags: ["settings", "plugins", "marketplace"],
  },
  {
    path: "/settings/organization/remote-access",
    description: "Remote access: manage remote session settings",
    tags: ["settings", "remote", "access"],
  },
  {
    path: "/settings/organization/safety",
    description: "PII & Safety: data safety and PII-handling settings",
    tags: ["settings", "safety", "pii", "security"],
  },
  {
    path: "/settings/organization/plans",
    description: "Plans: subscription plans and upgrades",
    tags: ["settings", "plans", "billing", "subscription"],
  },
];

// The pages this workspace can open.
export function openablePages(editorV2Enabled: boolean): GeniePage[] {
  return GENIE_PAGES.filter(
    (page) => !(editorV2Enabled && page.classicEditorOnly)
  );
}
